import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";

const empty = { full_name: "", phone: "", purpose: "", host: "", id_proof: "", expected_duration_min: 60 };

const Visitors = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const canManage = ["super_admin", "director", "receptionist"].includes(user?.role);

  const load = async () => {
    try { const { data } = await api.get("/visitors"); setList(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const checkin = async () => {
    try {
      const { data } = await api.post("/visitors/checkin", form);
      setList((l) => [data, ...l]); setOpen(false); setForm(empty);
      toast.success(`Pass: ${data.pass_code}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const checkout = async (id) => {
    try {
      const { data } = await api.post(`/visitors/${id}/checkout`);
      setList((l) => l.map((v) => v.id === id ? data : v));
      toast.success("Checked out");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="visitors-page">
      <PageHeader
        overline="Reception"
        title="Visitor Log"
        description="Capture visitors, issue passes and track check-in/check-out across the campus."
        actions={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="visitor-checkin-btn"><LogIn className="w-4 h-4 mr-1.5" /> Check-in</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Visitor Check-in</DialogTitle></DialogHeader>
              <div className="grid gap-4 mt-2">
                <Fld label="Full Name"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="visitor-name" /></Fld>
                <Fld label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Fld>
                <Fld label="Purpose"><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="visitor-purpose" /></Fld>
                <Fld label="Host (staff)"><Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} /></Fld>
                <Fld label="ID Proof"><Input value={form.id_proof} onChange={(e) => setForm({ ...form, id_proof: e.target.value })} placeholder="NIN / Driver's License" /></Fld>
                <Fld label="Expected Duration (min)"><Input type="number" value={form.expected_duration_min} onChange={(e) => setForm({ ...form, expected_duration_min: Number(e.target.value) })} /></Fld>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={checkin} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="visitor-submit">Check-in</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {list.length === 0 ? <EmptyState message="No visitors yet." /> : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-[#FDFBF7]">
              <TableHead>Pass</TableHead><TableHead>Name</TableHead><TableHead>Purpose</TableHead>
              <TableHead>Host</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map(v => (
                <TableRow key={v.id} data-testid={`visitor-row-${v.id}`}>
                  <TableCell className="font-mono text-xs">{v.pass_code}</TableCell>
                  <TableCell className="font-medium text-[#1B2C5A]">{v.full_name}</TableCell>
                  <TableCell>{v.purpose}</TableCell>
                  <TableCell>{v.host || "—"}</TableCell>
                  <TableCell className="text-xs">{new Date(v.check_in_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs">{v.check_out_at ? new Date(v.check_out_at).toLocaleString() : "—"}</TableCell>
                  <TableCell><span className={"text-xs px-2 py-1 rounded-full border " + (v.status === "in" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-700 border-slate-200")}>{v.status === "in" ? "On-site" : "Left"}</span></TableCell>
                  <TableCell className="text-right">
                    {canManage && v.status === "in" && <Button size="sm" variant="outline" onClick={() => checkout(v.id)} data-testid={`visitor-checkout-${v.id}`}><LogOut className="w-4 h-4 mr-1" /> Check-out</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default Visitors;
